import * as CryptoJS from 'crypto-js';
import _ from 'underscore';
import debug from 'debug';
import { Amplify } from 'aws-amplify';
import { Debug, observableToPromise } from '@ark7/utils';
import { FullDate } from '@ark7/common-models';
import { Identity } from '@ark7/identity';
import { Inject, Injectable } from '@angular/core';
import { LocalStorage, LocalStorageService } from 'ngx-webstorage';
import { ReplaySubject } from 'rxjs';
import { ResourceGlobalConfig } from '@ngx-resource/core';
import { TwoFAVerificationMethod } from '@ark7/core-business-models';
import {
  signOut as cognitoSignOut,
  signUp as cognitoSignUp,
} from 'aws-amplify/auth';

import {
  A7ResourcesCommonModuleConfig,
  A7_RESOURCES_CONFIG,
  A7_RESOURCES_USER,
} from '../declarations';
import { API_CLIENT_INFO, ApiClientInfo } from '../auth/request.interceptor';
import { AuthResource } from '../resources/auth-resource';
import { BasicUserResource } from '../resources/user-resource';
import { CookieDeviceIdReadyService } from './cookie-device-id-ready.service';

const d = debug('a7-resources-common:UserService');

export let userService: UserService;

export const USER_ID_KEY = 'userID';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  // We can not use tracking service due to circular deps.
  @LocalStorage()
  referrer: string;

  @LocalStorage()
  trackingCode: string;

  @LocalStorage()
  leadCode: string;

  @LocalStorage(USER_ID_KEY)
  userID: string;

  // Changes for event subscription
  userChange$ = new ReplaySubject<any>(1);
  // Current value, for get & set.
  private _currentUser: any;

  signingOut: boolean;

  get currentUser(): any {
    return this._currentUser;
  }
  set currentUser(user: any) {
    this._currentUser = user;
    this.userChange$.next(user);
  }

  // User signed out or timed out, after an active session.
  private _userSignedOut = false;
  get userSignedOut(): boolean {
    return this._userSignedOut;
  }

  constructor(
    @Inject(A7_RESOURCES_CONFIG) config: A7ResourcesCommonModuleConfig,
    @Inject(A7_RESOURCES_USER)
    private userResource: BasicUserResource<Identity>,
    private authResource: AuthResource,
    private localStorage: LocalStorageService,
    @Inject(API_CLIENT_INFO) private c: ApiClientInfo,
    private cookieDeviceIdReadyService: CookieDeviceIdReadyService,
  ) {
    Amplify.configure(config as any);

    if (userService == null) {
      userService = this;
    }

    this.localStorage.observe(USER_ID_KEY).subscribe((userID: string) => {
      if (userID == null && !this.signingOut && this.currentUser != null) {
        location.reload();
      }
    });
  }

  @Debug({ d })
  async signUp(req: SignUpRequest) {
    // Verify reCAPTCHA token if provided
    if (req.recaptchaToken) {
      await this.authResource.verifyRecaptcha({
        token: req.recaptchaToken,
        action: 'SIGN_UP',
        trackingCode: req.trackingCode
      });
    }

    const email = req.email.toLowerCase();
    const attributes = {
      email,
    };

    if (req.name != null) {
      attributes['given_name'] = req.name.first;
      attributes['family_name'] = req.name.last;
    }

    if (req.referrer != null) {
      attributes['custom:referrer'] = req.referrer;
    }

    if (req.mainSiteTOS) {
      attributes['custom:main_site_tos'] = 'true';
    }

    if (req.rentersTOS) {
      attributes['custom:renters_tos'] = 'true';
    }

    if (req.trackingCode) {
      attributes['custom:tracking_code'] = req.trackingCode;
    }
    if (req.leadCode) {
      attributes['custom:lead_code'] = req.leadCode;
    }

    const result = await cognitoSignUp({
      username: email,
      password: req.password,
      options: { userAttributes: attributes },
    });

    await this.authResource.confirmSignUp({ email });

    return result;
  }

  get isRenter(): boolean {
    return this._currentUser?.hasOwnProperty('activeContracts') ?? false;
  }

  async signIn(req: SignInRequest) {
    await this.authResource.login({
      username: req.email,
      password: this._s(req.email, req.password),
      recaptchaToken: req.recaptchaToken,
    });
    await this.fetch();
    this._userSignedOut = false;
  }

  signInGoogle(redirectTo?: string) {
    location.href = this.socialSignInUrl('/api/v1/auth/google', redirectTo);
  }

  signInFacebook(redirectTo?: string) {
    location.href = this.socialSignInUrl('/api/v1/auth/facebook', redirectTo);
  }

  signInApple(redirectTo?: string) {
    location.href = this.socialSignInUrl('/api/v1/auth/apple', redirectTo);
  }

  /**
   * Build a social sign-in URL. The redirect target is resolved in priority
   * order: an explicit `redirectTo` argument (e.g. passed by an embedded form
   * on a page without a `redirectTo` query param), then the current page's
   * `redirectTo` query param (the auth host bounces visitors here with
   * `?redirectTo=...`). The backend validates the target before redirecting.
   */
  private socialSignInUrl(path: string, redirectTo?: string): string {
    const current = new URL(window?.location.href);
    const target = redirectTo || current.searchParams.get('redirectTo');

    const url = new URL(
      ResourceGlobalConfig.pathPrefix + path,
      window?.location.href,
    );

    if (target) {
      url.searchParams.set('redirectTo', target);
    }

    const tracking = this.trackingParams();
    if (tracking) {
      const extra = new URLSearchParams(tracking.substring(1));
      extra.forEach((value, key) => url.searchParams.set(key, value));
    }
    return url.toString();
  }

  trackingParams() {
    const params = [];
    if (this.referrer) {
      params.push(`r=${this.referrer}`);
    }
    if (this.trackingCode) {
      params.push(`tc=${this.trackingCode}`);
    }
    if (this.leadCode) {
      params.push(`l=${this.leadCode}`);
    }
    return params.length > 0 ? '?' + params.join('&') : '';
  }

  @Debug({ d })
  async forgotPassword(req: ForgotPasswordRequest) {
    await this.authResource.forgotPassword({ email: req.email });
  }

  @Debug({ d })
  async forgotPasswordReset(req: IConfirmPassword) {
    await this.signOut();
    await this.authResource.forgotPasswordReset({
      email: req.email,
      code: req.code,
      password: req.password,
      firstName: req.firstName,
      lastName: req.lastName,
      dateOfBirth: req.dateOfBirth,
    });
    await this.fetch();
  }

  @Debug({ d })
  async signOut() {
    try {
      this.signingOut = true;
      await this.userResource.signOut();
      await cognitoSignOut();
    } catch (e) {
      console.log('ignored signOut error: ', e);
    }
    this._userSignedOut = true;
    this.currentUser = null;
    this.userID = null;
    this.signingOut = false;
  }

  resetUser() {
    this.currentUser = null;
    this.userID = null;
  }

  @Debug({ d })
  async fetch(options: UserFetchOptions = {}) {
    d('fetch: started with options', options);
    try {
      // Use retry logic to fetch user data
      const result = await this.fetchWithRetry(options);

      // Update currentUser and userID from the result
      this.currentUser = result;
      this.userID = result?._id.toString() ?? null;
      d('fetch: success, user ID = %s', this.userID);

      // Mark cookie device ID as ready on success
      this.cookieDeviceIdReadyService.markAsReady();
      d('fetch: marked cookie device ID as ready');
    } catch (error) {
      d('fetch: error occurred', error);
      // Even on error, mark as ready to avoid blocking other requests
      this.cookieDeviceIdReadyService.markAsReady();
      d('fetch: marked cookie device ID as ready (error path)');
      throw error;
    }
  }

  @Debug({ d })
  private async fetchWithRetry(
    options: UserFetchOptions,
    maxRetries: number = 3
  ): Promise<any> {
    d('fetchWithRetry: started with maxRetries = %d', maxRetries);
    let lastError: any;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      d('fetchWithRetry: attempt %d of %d', attempt, maxRetries);
      try {
        // Create a new Observable each attempt and convert to promise
        const userObserver = this.userResource.get(options);
        const result = await observableToPromise(userObserver);
        d('fetchWithRetry: success on attempt %d', attempt);
        return result;
      } catch (error) {
        lastError = error;
        d('fetchWithRetry: attempt %d failed with status = %s', attempt, error?.status);

        // If it's a 4xx error, don't retry
        if (error?.status >= 400 && error?.status < 500) {
          d('fetchWithRetry: 4xx error (%s), not retrying', error?.status);
          throw error;
        }

        // If it's a network error (status 0) or 5xx error, retry
        if (
          error?.status === 0 ||
          (error?.status >= 500 && error?.status < 600)
        ) {
          if (attempt < maxRetries) {
            // Exponential backoff: 1s, 2s, 4s
            const delay = Math.pow(2, attempt - 1) * 1000;
            d('fetchWithRetry: retrying after %dms (attempt %d of %d)', delay, attempt, maxRetries);
            await new Promise((resolve) => setTimeout(resolve, delay));
            continue;
          }
        }

        // Other errors don't retry
        d('fetchWithRetry: not retrying (error type: %s)', error?.status);
        throw error;
      }
    }

    d('fetchWithRetry: all %d attempts failed', maxRetries);
    throw lastError;
  }

  @Debug({ d })
  hasCurrentUser(): boolean {
    return this.currentUser != null;
  }

  async sendTwoFACode(method: TwoFAVerificationMethod) {
    await this.authResource.sendTwoFACode({ method });
  }

  async verifyTwoFACode(code: string, method: TwoFAVerificationMethod) {
    await this.authResource.verifyTwoFACode({ code, method });
  }

  _s(a: string, b: string) {
    const p = `${a}${b}`,
      c = this.c.clientSecret,
      k = `${c.substring(0, 6)}${c}${c.substring(c.length - 6)}`;

    try {
      const iv: CryptoJS.lib.WordArray = CryptoJS.lib.WordArray.random(16);

      const encrypted: CryptoJS.WordArray = CryptoJS.AES.encrypt(
        p,
        CryptoJS.enc.Utf8.parse(k),
        {
          iv: iv,
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7,
        },
      );

      return iv.toString(CryptoJS.enc.Hex) + encrypted.toString();
    } catch (e) {
      console.error(e);
    }
  }
}

export interface UserFetchOptions {
  advanced?: boolean;
}

export enum SignUpErrorCode {
  USERNAME_EXISTS = 'UsernameExistsException',
  INVALID_PASSWORD = 'InvalidPasswordException',
}

export enum SignInErrorCode {
  NOT_AUTHORIZED = 'NotAuthorizedException',
  USER_NOT_FOUND = 'UserNotFoundException',
  USER_NOT_CONFIRMED_EXCEPTION = 'UserNotConfirmedException',
}

export interface SignUpRequest {
  email: string;
  password: string;
  name?: {
    first: string;
    last: string;
  };
  referrer?: string;
  mainSiteTOS?: boolean;
  rentersTOS?: boolean;
  trackingCode?: string;
  leadCode?: string;
  recaptchaToken?: string;
}

export interface SignInRequest {
  email: string;
  password: string;
  recaptchaToken?: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ForgotPasswordResult {
  deliveryMedium: 'SMS' | 'EMAIL';
  destination: string;
}

export interface IConfirmPassword {
  email: string;
  code: string;
  password: string;
  firstName: string;
  lastName: string;
  dateOfBirth: FullDate;
}

export interface ConfirmSignUpRequest {
  email: string;
  code: string;
}

export function InjectUser(): PropertyDecorator {
  return (target: any, propertyKey: string | symbol) => {
    Object.defineProperty(target, propertyKey, {
      get: () => userService && userService.currentUser,
    });
  };
}
